import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, MapPin, MessageCircle } from 'lucide-react';
import { navigation, businessData } from '../data/mock';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloquear scroll del body cuando el menú está abierto
  useEffect(() => {
    if (isMenuOpen) {
      // Guardar la posición actual del scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurar el scroll
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Cerrar con tecla Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsMenuOpen(false);
    }
  };

  // Componente del overlay para renderizar via Portal
  const MobileMenuOverlay = () => (
    <div 
      className={`header__overlay ${isMenuOpen ? 'header__overlay--open' : ''}`}
      onClick={handleOverlayClick}
      aria-hidden={!isMenuOpen}
    >
      {/* Botón cerrar (X) */}
      <button 
        className="header__overlay-close"
        onClick={() => setIsMenuOpen(false)}
        aria-label="Cerrar menú"
      >
        <X size={28} />
      </button>

      <nav 
        className={`header__mobile-nav ${isMenuOpen ? 'header__mobile-nav--open' : ''}`}
        role="navigation"
        aria-label="Menú principal mobile"
      >
        <div className="header__mobile-logo">
          <img 
            src={businessData.logo} 
            alt={`${businessData.name} Logo`}
          />
        </div>
        
        {navigation.map((item, index) => (
          <a 
            key={item.name}
            href={item.href}
            className="header__mobile-link"
            onClick={handleNavClick}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {item.name}
          </a>
        ))}

        <div className="header__mobile-info">
          <div className="header__mobile-info-item">
            <MapPin size={18} />
            <span>{businessData.address.city}</span>
          </div>
        </div>

        <a 
          href={`https://wa.me/${businessData.contact.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary header__mobile-cta"
          onClick={handleNavClick}
        >
          WhatsApp
        </a>
      </nav>
    </div>
  );

  return (
    <>
      <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
        <div className="header__container container">
          <a href="#inicio" className="header__logo">
            <img 
              src={businessData.logo} 
              alt={`${businessData.name} Logo`}
              className="header__logo-img"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="header__nav header__nav--desktop">
            {navigation.map((item) => (
              <a 
                key={item.name}
                href={item.href}
                className="header__nav-link"
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <a 
            href={`https://wa.me/${businessData.contact.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="header__cta btn btn-primary"
          >
            <MessageCircle size={18} />
            Contactar
          </a>

          {/* Mobile Menu Button */}
          <button 
            className="header__menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - Renderizado via Portal fuera del header */}
      {createPortal(<MobileMenuOverlay />, document.body)}
    </>
  );
};

export default Header;
